import { useEffect, useRef } from "react";
import Konva from "konva";
import { Rect, Text, Group } from "react-konva";
import { useFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import { useIsCIMReorderEnabled } from "utils/url-utils";

type KonvaTooltipProps = {
  x: number;
  y: number;
  text: string;
  parentWidth: number;
  placement?: "top" | "right";
  anchorHeight?: number;
  offset?: number;
  bgColor?: string;
  textColor?: string;
  animationDuration?: number;
  maxWidth?: number;
};

const TRIANGLE_SIZE = 7;

const TEXT_STYLE: Partial<Konva.TextConfig> = {
  fontFamily: "Poppins",
  fontSize: 10,
  fontVariant: "400",
  lineHeight: 1.5,
  wrap: "word",
};

function centerWidth(width1: number, width2: number) {
  return (width1 - width2) / 2;
}

const Triangle = ({
  width = 7,
  height = 7,
  x = 0,
  y = 0,
  direction = "down",
  fill = "#DEE1EB",
}: {
  width?: number;
  height?: number;
  x?: number;
  y?: number;
  direction?: "down" | "left";
  fill?: string;
}) => {
  const rotation = direction === "left" ? 45 : -45;

  return (
    <Rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      rotation={rotation}
      cornerRadius={1}
      offsetX={width / 2}
      offsetY={height / 2}
    />
  );
};

function calculateTextDimensions(
  text: string,
  maxWidth: number,
  config: Partial<Konva.TextConfig> = {}
): { width: number; height: number } {
  // Create a temporary Konva.Text node for measurement
  const textNodeMaxWidth = new Konva.Text({
    text,
    width: maxWidth,
    wrap: "word",
    ...config,
  });

  const textNode = new Konva.Text({
    text,
    wrap: "word",
    ...config,
  });

  // Get the dimensions
  const width = Math.min(textNode.width(), textNodeMaxWidth.width(), maxWidth);
  const height = Math.max(textNode.height(), textNodeMaxWidth.height());

  return { width, height };
}

const defaultBgColorStandalone = "#113357";
const defaultBgColorCim = "#1e1e1e";

export default function KonvaTooltip({
  x,
  y,
  parentWidth,
  text,
  placement = "top",
  anchorHeight = 0,
  offset,
  bgColor,
  textColor = "white",
  animationDuration = 0,
  maxWidth = 150,
}: KonvaTooltipProps) {
  const groupRef = useRef<Konva.Group>(null);
  const tweenRef = useRef<Konva.Tween | null>(null);
  const isCIM = useIsCIMReorderEnabled();
  const newFloaters = useFeatureFlag("cim-new-floaters-style");
  const newStyle = isCIM && newFloaters;

  bgColor ??= newStyle ? defaultBgColorCim : defaultBgColorStandalone;

  const size = calculateTextDimensions(text, maxWidth, TEXT_STYLE);

  const isRightPlacement = placement === "right";
  const gap = offset ?? 8;

  const tooltipX = isRightPlacement ? x + parentWidth + gap : x + centerWidth(parentWidth, size.width + 20);
  const tooltipY = isRightPlacement
    ? y + (anchorHeight ? centerWidth(anchorHeight, size.height + 20) : 0)
    : y - size.height;

  const textX = isRightPlacement ? tooltipX + 10 : x + centerWidth(parentWidth, size.width);
  const textY = tooltipY + 10;

  const triangleDirection = isRightPlacement ? "left" : "down";
  const triangleX = isRightPlacement ? tooltipX : x + centerWidth(parentWidth, TRIANGLE_SIZE);
  const triangleY = isRightPlacement ? tooltipY + centerWidth(size.height + 27, TRIANGLE_SIZE) : y + 19;

  useEffect(() => {
    const group = groupRef.current;
    if (!group || animationDuration <= 0) return;

    tweenRef.current = new Konva.Tween({
      node: group,
      duration: animationDuration,
      opacity: 1,
      easing: Konva.Easings.EaseOut,
    });
    tweenRef.current.play();

    return () => {
      if (tweenRef.current) {
        tweenRef.current.destroy();
      }
    };
  }, [animationDuration]);

  return (
    <Group ref={groupRef} opacity={animationDuration > 0 ? 0 : 1}>
      <Rect
        fill={bgColor}
        y={tooltipY}
        x={tooltipX}
        width={size.width + 20}
        height={size.height + 20}
        cornerRadius={[6, 6, 6, 6]}
        shadowColor="rgba(0, 0, 0, 0.2)"
        shadowBlur={8}
        shadowOffset={{ x: 0, y: 2 }}
        shadowOpacity={0.8}
      />
      <Text y={textY} x={textX} text={text} fill={textColor} width={size.width} height={size.height} {...TEXT_STYLE} />
      {!newStyle && (
        <Triangle
          direction={triangleDirection}
          x={triangleX}
          y={triangleY}
          width={TRIANGLE_SIZE}
          height={TRIANGLE_SIZE}
          fill={bgColor}
        />
      )}
    </Group>
  );
}
